import { Plus } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { Task, TaskStatus } from "@/api/queries";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import { compareTasks, taskStatuses } from "./lib";
import { TaskCard } from "./TaskCard";
import { TaskStatusBadge } from "./TaskStatus";

type TaskBoardProps = {
  tasks: Task[];
  names: Map<string, string>;
  today: string;
  canCreate: boolean;
  canMove: (task: Task) => boolean;
  onOpen: (task: Task) => void;
  onCreate: (status: TaskStatus) => void;
  onMove: (task: Task, status: TaskStatus) => void;
};

/** Kanban board: drag a card to another column, or use the card menu. */
export function TaskBoard({
  tasks,
  names,
  today,
  canCreate,
  canMove,
  onOpen,
  onCreate,
  onMove,
}: TaskBoardProps) {
  const { t } = useTranslation();
  const [dragged, setDragged] = useState<Task | null>(null);
  const [over, setOver] = useState<TaskStatus | null>(null);

  return (
    <div className="grid gap-4 lg:grid-cols-3">
      {taskStatuses.map((status) => {
        const column = tasks.filter((task) => task.status === status).sort(compareTasks);
        const droppable = dragged !== null && dragged.status !== status;
        return (
          <section
            key={status}
            aria-label={t(`tasks.status.${status}`)}
            onDragOver={(e) => {
              if (!droppable) return;
              e.preventDefault();
              e.dataTransfer.dropEffect = "move";
              setOver(status);
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(null);
            }}
            onDrop={(e) => {
              e.preventDefault();
              if (dragged && droppable) onMove(dragged, status);
              setDragged(null);
              setOver(null);
            }}
            className={cn(
              "flex min-h-40 flex-col gap-3 rounded-xl border border-transparent bg-surface-muted p-3 transition-colors",
              over === status && "border-primary-solid bg-primary-soft",
            )}
          >
            <header className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <TaskStatusBadge status={status} />
                <span className="text-sm text-muted tabular-nums">{column.length}</span>
              </div>
              {canCreate && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7"
                  aria-label={`${t("tasks.createTitle")}: ${t(`tasks.status.${status}`)}`}
                  onClick={() => onCreate(status)}
                >
                  <Plus />
                </Button>
              )}
            </header>
            <ul className="flex flex-col gap-2">
              {column.map((task) => (
                <TaskCard
                  key={task.id}
                  task={task}
                  assigneeName={task.assignee_id ? names.get(task.assignee_id) : undefined}
                  today={today}
                  canMove={canMove(task)}
                  dragging={dragged?.id === task.id}
                  onOpen={() => onOpen(task)}
                  onMove={(s) => onMove(task, s)}
                  onDragStart={() => setDragged(task)}
                  onDragEnd={() => {
                    setDragged(null);
                    setOver(null);
                  }}
                />
              ))}
            </ul>
            {droppable && column.length === 0 && (
              <p className="rounded-lg border border-dashed px-3 py-6 text-center text-sm text-muted">
                {t("tasks.dropHere")}
              </p>
            )}
          </section>
        );
      })}
    </div>
  );
}
